import { BitmapText, Container, Graphics } from "pixi.js";
import { noteLabel, type NoteNotation } from "@/lib/music/notation";
import { getNotationStore } from "@/lib/music/notation-store";
import { PianoLayout } from "@/lib/piano/piano-layout";
import { PianoComponent } from "@/lib/studio/view/components/piano-component";

export type OctaveGridProps = {
  width: number;
  height: number;
};

/** MIDI numbers for C notes (C1 through C7) */
const OCTAVE_C_MIDIS = [24, 36, 48, 60, 72, 84, 96];

/** Middle C MIDI number */
const MIDDLE_C_MIDI = 60;

// Line styling
const LINE_COLOR = 0x8b5cf6;
const LINE_ALPHA = 0.08;
const LINE_ALPHA_MIDDLE_C = 0.14;
const LINE_WIDTH = 1;

// Alternating zone styling
const ZONE_COLOR = 0xffffff;
const ZONE_ALPHA = 0.018;

// Label styling — neutral chrome, deliberately recessive so the octave
// markers read as wayfinding (consulted on demand), not as brand accents.
const LABEL_TINT = 0xcbd5e1; // slate-300, neutral
const LABEL_ALPHA = 0.32;
const LABEL_FONT_SIZE = 10;
const LABEL_OFFSET_Y = 6;

// Middle C (C4) is the single anchor users orient from: it gets the only
// accent (violet + a small dot), everything else stays quietly neutral.
const MIDDLE_C_TINT = 0xa78bfa;
const MIDDLE_C_ALPHA = 0.6;
const MIDDLE_C_DOT_RADIUS = 2;
const MIDDLE_C_DOT_GAP = 5;

type OctaveBoundary = {
  midi: number;
  x: number;
  label: string;
};

export class OctaveGrid {
  private container: Container;
  private zonesGraphics: Graphics;
  private linesGraphics: Graphics;
  private accentGraphics: Graphics;
  private labels: BitmapText[] = [];

  private previousProps?: OctaveGridProps;
  private previousWhiteKeyWidth = -1;
  private previousOffsetX = NaN;
  private previousNotation: NoteNotation | null = null;
  private previousBoundaries: OctaveBoundary[] = [];

  constructor(
    private stage: Container,
    private layout: PianoLayout,
    private pianoComponent: PianoComponent,
  ) {
    this.container = new Container();
    this.stage.addChild(this.container);

    this.zonesGraphics = new Graphics();
    this.container.addChild(this.zonesGraphics);

    this.linesGraphics = new Graphics();
    this.container.addChild(this.linesGraphics);

    // Accent layer (the middle-C dot) sits above the lines/zones
    this.accentGraphics = new Graphics();
    this.container.addChild(this.accentGraphics);
  }

  destroy(): void {
    for (const label of this.labels) {
      label.destroy();
    }
    this.labels.length = 0;
    this.accentGraphics.destroy();
    this.linesGraphics.destroy();
    this.zonesGraphics.destroy();
    this.container.destroy({ children: true });
  }

  draw(props: OctaveGridProps): void {
    const { whiteKeyWidth, offsetX } = this.pianoComponent.getProjection();

    if (!this.shouldRedraw(props, whiteKeyWidth, offsetX)) {
      return;
    }

    // Use the boundaries computed and cached by shouldRedraw
    const boundaries = this.previousBoundaries;
    const { width, height } = props;

    this.drawZones(boundaries, width, height);
    this.drawLines(boundaries, height);
    this.drawLabels(boundaries);
  }

  private shouldRedraw(props: OctaveGridProps, whiteKeyWidth: number, offsetX: number): boolean {
    const notation = getNotationStore().noteNotation.value;

    // Les frontières ne dépendent que de ces quatre entrées : tant qu'elles
    // sont les mêmes, inutile de les recalculer (sept objets par image, à
    // chaque image) pour constater qu'elles n'ont pas bougé.
    if (
      this.previousProps
      && this.previousWhiteKeyWidth === whiteKeyWidth
      && this.previousOffsetX === offsetX
      && this.previousNotation === notation
    ) {
      const hasChanged =
        this.previousProps.width !== props.width
        || this.previousProps.height !== props.height;

      this.previousProps = props;

      return hasChanged;
    }

    const boundaries = this.computeBoundaries(whiteKeyWidth, offsetX, notation);

    if (!this.previousProps) {
      this.previousProps = props;
      this.previousWhiteKeyWidth = whiteKeyWidth;
      this.previousOffsetX = offsetX;
      this.previousNotation = notation;
      this.previousBoundaries = boundaries;
      return true;
    }

    const hasChanged =
      this.previousProps.width !== props.width
      || this.previousProps.height !== props.height
      || this.previousWhiteKeyWidth !== whiteKeyWidth
      || !this.sameBoundaries(this.previousBoundaries, boundaries);

    this.previousProps = props;
    this.previousWhiteKeyWidth = whiteKeyWidth;
    this.previousOffsetX = offsetX;
    this.previousNotation = notation;
    this.previousBoundaries = boundaries;

    return hasChanged;
  }

  private sameBoundaries(a: OctaveBoundary[], b: OctaveBoundary[]): boolean {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
      // `label` is compared too: switching the note-name notation leaves midi
      // and x untouched, and without this the stale spelling would stay on
      // screen until an unrelated redraw happened to come along.
      if (a[i].midi !== b[i].midi || a[i].x !== b[i].x || a[i].label !== b[i].label) return false;
    }
    return true;
  }

  private computeBoundaries(
    whiteKeyWidth: number,
    offsetX: number,
    notation: NoteNotation,
  ): OctaveBoundary[] {
    const boundaries: OctaveBoundary[] = [];

    for (const midi of OCTAVE_C_MIDIS) {
      const key = this.layout.getKeyByMidi(midi);
      if (!key) continue;

      // C is always a white key, its left edge is the octave boundary
      const x = Math.round(key.whiteIndex * whiteKeyWidth) + offsetX;
      const octaveNumber = Math.floor(midi / 12) - 1;

      boundaries.push({
        midi,
        x,
        label: noteLabel(`C${octaveNumber}`, notation),
      });
    }

    return boundaries;
  }

  private drawZones(boundaries: OctaveBoundary[], width: number, height: number): void {
    this.zonesGraphics.clear();

    for (let i = 0; i < boundaries.length - 1; i++) {
      const octaveNumber = Math.floor(boundaries[i].midi / 12) - 1;

      // Shade even-numbered octaves for alternating zebra pattern
      if (octaveNumber % 2 !== 0) continue;

      const xStart = boundaries[i].x;
      const xEnd = boundaries[i + 1].x;

      this.zonesGraphics
        .rect(xStart, 0, xEnd - xStart, height)
        .fill({ color: ZONE_COLOR, alpha: ZONE_ALPHA });
    }
  }

  private drawLines(boundaries: OctaveBoundary[], height: number): void {
    this.linesGraphics.clear();

    for (const boundary of boundaries) {
      const alpha = boundary.midi === MIDDLE_C_MIDI ? LINE_ALPHA_MIDDLE_C : LINE_ALPHA;

      this.linesGraphics
        .moveTo(boundary.x, 0)
        .lineTo(boundary.x, height)
        .stroke({ color: LINE_COLOR, alpha, width: LINE_WIDTH });
    }
  }

  private drawLabels(boundaries: OctaveBoundary[]): void {
    // Hide all existing labels
    for (const label of this.labels) {
      label.visible = false;
    }

    this.accentGraphics.clear();

    for (let i = 0; i < boundaries.length; i++) {
      const boundary = boundaries[i];
      const label = this.getOrCreateLabel(i);
      const isMiddleC = boundary.midi === MIDDLE_C_MIDI;

      // Octave span: from this C boundary to the next one (fallback to the
      // previous gap for the last octave, which has no following boundary).
      const nextX = boundaries[i + 1]?.x;
      const octaveWidth =
        nextX !== undefined
          ? nextX - boundary.x
          : boundary.x - (boundaries[i - 1]?.x ?? boundary.x);

      label.text = boundary.label;
      label.x = Math.round(boundary.x + octaveWidth / 2 - label.width / 2);
      label.y = LABEL_OFFSET_Y;
      label.tint = isMiddleC ? MIDDLE_C_TINT : LABEL_TINT;
      label.alpha = isMiddleC ? MIDDLE_C_ALPHA : LABEL_ALPHA;
      label.visible = true;

      // Single accent: a small dot left of the middle-C label so the eye has
      // exactly one anchor to settle on instead of seven equal markers.
      if (isMiddleC) {
        const dotY = label.y + label.height / 2;

        this.accentGraphics
          .circle(label.x - MIDDLE_C_DOT_GAP, dotY, MIDDLE_C_DOT_RADIUS)
          .fill({ color: MIDDLE_C_TINT, alpha: MIDDLE_C_ALPHA });
      }
    }
  }

  private getOrCreateLabel(index: number): BitmapText {
    if (index < this.labels.length) {
      return this.labels[index];
    }

    const label = new BitmapText({
      text: "",
      style: {
        fontFamily: "Inter, system-ui, sans-serif",
        fontSize: LABEL_FONT_SIZE,
        fontWeight: "500",
        // White base so per-label `tint` controls the actual color.
        fill: 0xffffff,
      },
    });

    this.container.addChild(label);
    this.labels.push(label);

    return label;
  }
}
